// INACTIVITY-UI-SIMPLIFY-004 — pure helpers for the HR-facing summary (presentation only; no calculation changes).
// eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional ET time-only (same as InactivityReportPage)
export const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '—';
export const fmtMin = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${String(Math.round(m % 60)).padStart(2, '0')}m` : `${Math.round(m)}m`;
export const fmtMinLong = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${String(Math.round(m % 60)).padStart(2, '0')} min` : `${Math.round(m)} min`;

// Periods that still need attention: unresolved gaps only, in time order. Amount shown = unexplained minutes.
export const unexplainedPeriods = (gaps) =>
  (gaps || [])
    .filter((g) => !g.resolved)
    .slice()
    .sort((a, b) => new Date(a.inactive_from) - new Date(b.inactive_from))
    .map((g) => ({
      from: g.inactive_from,
      to: g.inactive_to,
      minutes: g.unexplained_minutes,
      ongoing: !!g.ongoing,
      promptUnanswered: !!g.prompts?.some((p) => !p.responded),
      explanation: g.explanation || null,
    }));

// INACTIVITY-EXPLANATION-001 — per-employee review status labels (display only)
export const REVIEW_STATUS = {
  needs_review: { label: 'Needs Review', tone: 'amber' },
  reviewed: { label: 'Reviewed', tone: 'green' },
  updated_since_review: { label: 'Updated Since Review', tone: 'blue' },
};

export const totalUnexplained = (periods) => periods.reduce((s, p) => s + (p.minutes || 0), 0);

// INACTIVITY-UI-FINAL-006 — display labels only; stored/API statuses unchanged.
export const EMP_STATUS = {
  unexplained: { label: 'Needs Attention', cls: 'bg-red-100 text-red-700' },
  partially_explained: { label: 'Partially Covered', cls: 'bg-amber-100 text-amber-800' },
  explained: { label: 'Covered', cls: 'bg-emerald-100 text-emerald-700' },
};

// After (re)loading the day list: keep the current day when asked and still present, else first day needing review, else first.
export const pickSelectedDay = (days, current, keepSelection) => {
  if (keepSelection && days.some((d) => d.date === current)) return current;
  return (days.find((d) => d.status === 'needs_review' || d.status === 'updated_since_review') || days[0])?.date || null;
};

export const isValidRange = (r) => !!(r?.start && r?.end && r.start <= r.end);

// INACTIVITY-EXPLANATION-001 — merge a save/review response into one employee row (mirrors backend _review_signature).
const periodKey = (g) => `${g.inactive_from}|${g.inactive_to}`;
const reviewSignature = (gaps, explanations) => ({
  gap_keys: gaps.map(periodKey).slice().sort(),
  explanations: explanations.map((e) => `${e.period_key}#${e.version || 1}`).sort(),
});
const sameSignature = (a, b) => !!a && !!b && JSON.stringify(a.gap_keys) === JSON.stringify(b.gap_keys) && JSON.stringify(a.explanations) === JSON.stringify(b.explanations);

export const applyExplanationPatch = (emp, data) => {
  const explanations = data.explanations ?? emp.explanations ?? [];
  const byKey = Object.fromEntries(explanations.map((x) => [x.period_key, x]));
  const gaps = (emp.gaps || []).map((g) => {
    const x = byKey[periodKey(g)];
    return { ...g, explanation: x ? { id: x.id, explanation: x.explanation, created_by_name: x.created_by_name, created_by_role: x.created_by_role, created_at: x.created_at, updated_by_name: x.updated_by_name, updated_by_role: x.updated_by_role, updated_at: x.updated_at, version: x.version } : null };
  });
  const review = data.review !== undefined ? data.review : emp.review;
  const snapshot = data.review_snapshot !== undefined ? data.review_snapshot : emp.review_snapshot;
  const review_status = !review ? 'needs_review' : sameSignature(snapshot, reviewSignature(gaps, explanations)) ? 'reviewed' : 'updated_since_review';
  return { ...emp, gaps, explanations, review, review_snapshot: snapshot, review_status,
    explained_periods: gaps.filter((g) => !g.resolved && g.explanation).length };
};
